// ============================================
// Hero Slider - Embla Carousel Implementation
// 80/10/10 Peek Layout
// ============================================

import EmblaCarousel from 'embla-carousel';

// ============================================
// Configuration Constants
// ============================================
const CLONE_BREAKPOINT = 2355; // px - breakpoint to clone slides
const SLIDE_MAX_WIDTH = 1177; // px - max-width from SCSS

/**
 * HeroSlider Class
 * Manages carousel functionality for both desktop and mobile hero sections
 * Supports responsive behaviors:
 * - >= 2355px: Clone slides to fill viewport
 * - > total slides width: Center mode (disable carousel)
 */
export class HeroSlider {
  constructor(selector, options = {}) {
    this.selector = selector;
    this.container = typeof selector === 'string'
      ? document.querySelector(selector)
      : selector;

    if (!this.container) {
      console.warn(`HeroSlider: Container not found for selector "${selector}"`);
      return;
    }

    // Default options
    this.options = {
      autoplay: 5000,
      loop: true,
      pauseOnHover: true,
      ...options
    };

    // DOM elements
    this.viewport = this.container.querySelector('[data-embla-viewport]');
    this.slidesContainer = this.viewport?.querySelector('.c-hero-section__container, .c-mobile-hero__container');
    this.pagination = this.container.querySelector('.c-hero-section__pagination, .c-mobile-hero__pagination');
    this.dots = this.container.querySelectorAll('[data-embla-dot]');
    this.paginationWrapper = this.container.querySelector('.c-hero-section__pagination-wrapper, .c-mobile-hero__pagination-wrapper');

    // State
    this.autoplayTimer = null;
    this.isPlaying = true;
    this.isCenterMode = false;
    this.isCloned = false;
    this.originalSlides = [];
    this.originalSlideCount = 0;
    this.resizeDebounceTimer = null;

    // Store original slides reference before any cloning
    this.storeOriginalSlides();

    this.init();
  }

  /**
   * Store reference to original slides for cloning/restore
   */
  storeOriginalSlides() {
    if (!this.slidesContainer) return;
    this.originalSlides = Array.from(this.slidesContainer.querySelectorAll('[data-embla-slide]'));
    this.originalSlideCount = this.originalSlides.length;
  }

  /**
   * Initialize the carousel
   */
  init() {
    if (!this.viewport || !this.slidesContainer) {
      console.warn('HeroSlider: Viewport or slides container not found');
      return;
    }

    // Check and apply responsive behavior
    this.applyResponsiveBehavior();

    // Set up resize listener
    this.setupResizeListener();
  }

  /**
   * Check responsive conditions and apply appropriate behavior
   */
  applyResponsiveBehavior() {
    const windowWidth = window.innerWidth;
    const totalSlidesWidth = this.originalSlideCount * SLIDE_MAX_WIDTH;

    // Center mode: window width > total slides width
    if (windowWidth > totalSlidesWidth) {
      this.enableCenterMode();
      return;
    }

    // Not in center mode, remove center mode class if present
    this.disableCenterMode();

    // Clone mode: window width >= 2355px
    if (windowWidth >= CLONE_BREAKPOINT && !this.isCloned) {
      this.cloneSlides();
    } else if (windowWidth < CLONE_BREAKPOINT && this.isCloned) {
      this.removeClonedSlides();
    }

    // Initialize or reinitialize carousel if not in center mode
    if (!this.embla) {
      this.initCarousel();
    }
  }

  /**
   * Enable center mode - disable carousel, center all slides statically
   */
  enableCenterMode() {
    if (this.isCenterMode) return;

    this.isCenterMode = true;

    // Destroy carousel instance
    this.destroy();

    // Add center mode class for CSS styling
    this.container.classList.add('c-hero-section--center-mode');

    // Ensure all original slides are present (remove clones)
    this.removeClonedSlides();

    // Hide pagination
    if (this.paginationWrapper) {
      this.paginationWrapper.style.display = 'none';
    }
  }

  /**
   * Disable center mode - re-enable carousel
   */
  disableCenterMode() {
    if (!this.isCenterMode) return;

    this.isCenterMode = false;

    // Remove center mode class
    this.container.classList.remove('c-hero-section--center-mode');

    // Show pagination
    if (this.paginationWrapper) {
      this.paginationWrapper.style.display = '';
    }
  }

  /**
   * Clone slides to fill viewport at >= 2355px
   */
  cloneSlides() {
    if (this.isCloned || !this.slidesContainer || this.originalSlides.length === 0) return;

    this.isCloned = true;

    // Clone original slides and append them
    this.originalSlides.forEach((slide, index) => {
      const clone = slide.cloneNode(true);
      clone.classList.add('is-cloned');
      clone.dataset.cloneIndex = index;
      // Remove aria-label from clones for accessibility
      clone.removeAttribute('aria-label');
      this.slidesContainer.appendChild(clone);
    });

    // Reinitialize carousel to recognize new slides
    if (this.embla) {
      this.embla.destroy();
      this.initCarousel();
    }
  }

  /**
   * Remove cloned slides when dropping below breakpoint
   */
  removeClonedSlides() {
    if (!this.isCloned || !this.slidesContainer) return;

    this.isCloned = false;

    // Remove all cloned slides
    const clones = this.slidesContainer.querySelectorAll('.is-cloned');
    clones.forEach(clone => clone.remove());

    // Reinitialize carousel
    if (this.embla) {
      this.embla.destroy();
      this.initCarousel();
    }
  }

  /**
   * Initialize the Embla carousel
   */
  initCarousel() {
    if (!this.viewport) return;

    // Initialize Embla with peek effect (80% slide width)
    this.embla = EmblaCarousel(this.viewport, {
      loop: this.options.loop,
      align: 'center',
      containScroll: false,
      skipSnaps: false,
      slidesToScroll: 1,
      startIndex: 0
    });

    // Set up event handlers
    this.setupPagination();
    this.setupAutoplay();
    this.setupKeyboard();
    this.setupAccessibility();
    this.setupOverlayEffect();
    this.setupPaginationPosition();

    // Initial states
    this.updateDots();
    this.updateOverlayEffect();
    this.updatePaginationPosition();
  }

  /**
   * Set up resize listener with debounce
   */
  setupResizeListener() {
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeDebounceTimer);
      this.resizeDebounceTimer = setTimeout(() => {
        this.applyResponsiveBehavior();
      }, 150);
    });
  }

  /**
   * Set up overlay effect on non-selected slides (rgba(0,0,0,0.3) per Figma)
   */
  setupOverlayEffect() {
    // Update overlay on slide selection
    this.embla.on('select', () => {
      this.updateOverlayEffect();
    });

    this.embla.on('scroll', () => {
      this.updateOverlayEffect();
    });
  }

  /**
   * Update overlay effect based on selected slide
   * Applies rgba(0,0,0,0.3) overlay to non-selected slides via CSS
   * Handles cloned slides - only one slide is "selected" at a time
   */
  updateOverlayEffect() {
    if (!this.embla) return;

    const slides = this.embla.slideNodes();
    const selectedIndex = this.embla.selectedScrollSnap();

    slides.forEach((slide, index) => {
      // Add/remove is-selected class for CSS overlay effect
      if (index === selectedIndex) {
        slide.classList.add('is-selected');
      } else {
        slide.classList.remove('is-selected');
      }
    });
  }

  /**
   * Set up pagination position tracking
   * Positions pagination dots at bottom of active slide's content area
   */
  setupPaginationPosition() {
    if (!this.pagination) return;

    // Update position on scroll and resize
    this.embla.on('scroll', () => {
      this.updatePaginationPosition();
    });

    this.embla.on('select', () => {
      this.updatePaginationPosition();
    });

    window.addEventListener('resize', () => {
      this.updatePaginationPosition();
    });
  }

  /**
   * Update pagination position to align with active slide's content area
   */
  updatePaginationPosition() {
    if (!this.pagination) return;

    const slides = this.embla.slideNodes();
    const selectedIndex = this.embla.selectedScrollSnap();
    const activeSlide = slides[selectedIndex];

    if (!activeSlide) return;

    const contentArea = activeSlide.querySelector('.c-hero-section__content');
    if (!contentArea) return;

    // Get the slide's position relative to the viewport
    const slideRect = activeSlide.getBoundingClientRect();
    const containerRect = this.container.getBoundingClientRect();

    // Calculate content area position within the slide
    // Content area is on the right side, after the image (55.6% width)
    const slideWidth = slideRect.width;
    const imageWidth = slideWidth * 0.556; // Image takes ~55.6% of slide
    const contentPadding = 44; // Content area padding

    // Calculate left position: slide left + image width + content padding
    const leftPosition = slideRect.left - containerRect.left + imageWidth + contentPadding;

    // Apply position
    this.pagination.style.left = `${leftPosition}px`;
  }

  /**
   * Set up pagination dots
   * Maps dots to original slides (accounting for clones)
   */
  setupPagination() {
    if (!this.dots.length) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        // Map dot index to actual slide index (considering clones)
        const targetIndex = this.mapDotToSlideIndex(index);
        this.embla.scrollTo(targetIndex);
        this.resetAutoplay();
      });
    });

    // Update active dot on scroll
    this.embla.on('select', () => {
      this.updateDots();
    });
  }

  /**
   * Map original dot index to actual slide index
   * When cloned, dots map to first set of slides
   */
  mapDotToSlideIndex(dotIndex) {
    if (!this.isCloned) return dotIndex;
    // When cloned, scroll to the slide in the first/original set
    return dotIndex;
  }

  /**
   * Map actual slide index back to original dot index
   */
  mapSlideToDotIndex(slideIndex) {
    if (!this.isCloned) return slideIndex;
    // Use modulo to map cloned slide back to original dot
    return slideIndex % this.originalSlideCount;
  }

  /**
   * Update active pagination dot
   * Accounts for cloned slides using modulo mapping
   */
  updateDots() {
    if (!this.dots.length) return;

    const selectedIndex = this.embla.selectedScrollSnap();
    // Map selected slide to original dot index
    const dotIndex = this.mapSlideToDotIndex(selectedIndex);

    this.dots.forEach((dot, index) => {
      const isActive = index === dotIndex;
      dot.classList.toggle('c-hero-section__dot--active', isActive);
      dot.classList.toggle('c-mobile-hero__dot--active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  /**
   * Set up autoplay functionality
   */
  setupAutoplay() {
    if (!this.options.autoplay) return;

    this.startAutoplay();

    if (this.options.pauseOnHover) {
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => this.startAutoplay());
    }

    // Pause when tab is hidden
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    });
  }

  /**
   * Start autoplay timer
   */
  startAutoplay() {
    if (this.autoplayTimer) return;

    this.isPlaying = true;
    this.autoplayTimer = setInterval(() => {
      if (this.options.loop) {
        this.embla.scrollNext();
      } else {
        // If at the end, go back to start
        if (!this.embla.canScrollNext()) {
          this.embla.scrollTo(0);
        } else {
          this.embla.scrollNext();
        }
      }
    }, this.options.autoplay);
  }

  /**
   * Stop autoplay timer
   */
  stopAutoplay() {
    this.isPlaying = false;
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  /**
   * Reset autoplay timer (call after user interaction)
   */
  resetAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  /**
   * Set up keyboard navigation
   */
  setupKeyboard() {
    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.embla.scrollPrev();
        this.resetAutoplay();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.embla.scrollNext();
        this.resetAutoplay();
      }
    });

    // Make container focusable
    this.container.setAttribute('tabindex', '0');
    this.container.setAttribute('role', 'region');
    this.container.setAttribute('aria-roledescription', 'carousel');
  }

  /**
   * Set up accessibility attributes
   */
  setupAccessibility() {
    const slides = this.viewport.querySelectorAll('[data-embla-slide]');

    slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', `Slide ${index + 1} of ${slides.length}`);
    });

    // Set up dots accessibility
    this.dots.forEach((dot, index) => {
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
    });
  }

  /**
   * Destroy the carousel instance
   * Clean up timers and event listeners
   */
  destroy() {
    this.stopAutoplay();
    if (this.embla) {
      this.embla.destroy();
      this.embla = null;
    }
    // Clear resize debounce timer
    if (this.resizeDebounceTimer) {
      clearTimeout(this.resizeDebounceTimer);
    }
    // Remove window resize listener to prevent memory leaks
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  /**
   * Scroll to a specific slide
   */
  scrollTo(index) {
    this.embla.scrollTo(index);
  }

  /**
   * Get current slide index
   */
  getSelectedIndex() {
    return this.embla.selectedScrollSnap();
  }
}

/**
 * Initialize all hero sliders on the page
 */
export function initHeroSliders() {
  const sliders = [];

  // Desktop hero sliders
  document.querySelectorAll('.js-hero-slider').forEach(el => {
    const autoplay = el.dataset.heroAutoplay
      ? parseInt(el.dataset.heroAutoplay, 10)
      : 5000;

    const slider = new HeroSlider(el, {
      autoplay,
      loop: true,
      pauseOnHover: true
    });

    sliders.push(slider);
  });

  // Mobile hero sliders
  document.querySelectorAll('.js-mobile-hero-slider').forEach(el => {
    const autoplay = el.dataset.heroAutoplay
      ? parseInt(el.dataset.heroAutoplay, 10)
      : 5000;

    const slider = new HeroSlider(el, {
      autoplay,
      loop: true,
      pauseOnHover: true
    });

    sliders.push(slider);
  });

  return sliders;
}

export default HeroSlider;
